import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';

export const useApprenants = (options = {}) => {
  const { autoLoad = true, filters = {} } = options;
  const filtersRef = useRef(filters);

  const [apprenants, setApprenants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadApprenants = useCallback(async (customFilters = {}) => {
    setLoading(true);
    setError(null);

    try {
      const params = { ...filtersRef.current, ...customFilters };
      const response = await api.apprenants.getAll(params);
      setApprenants(response.apprenants || []);
      return response.apprenants || [];
    } catch (err) {
      const message = err.message || 'Erreur lors du chargement des apprenants';
      setError(message);
      console.error('Erreur loadApprenants:', err);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const createApprenant = useCallback(async (data) => {
    setError(null);

    try {
      const response = await api.apprenants.create(data);
      const newApprenant = response.apprenant;
      setApprenants(prev => [newApprenant, ...prev]);
      return { success: true, apprenant: newApprenant };
    } catch (err) {
      const message = err.message || 'Erreur lors de la création de l\'apprenant';
      setError(message);
      return { success: false, error: message };
    }
  }, []);

  const updateApprenant = useCallback(async (id, data) => {
    setError(null);

    try {
      const response = await api.apprenants.update(id, data);
      const updated = response.apprenant;
      setApprenants(prev => prev.map(item => item.id === id ? updated : item));
      return { success: true, apprenant: updated };
    } catch (err) {
      const message = err.message || 'Erreur lors de la mise à jour de l\'apprenant';
      setError(message);
      return { success: false, error: message };
    }
  }, []);

  const deleteApprenant = useCallback(async (id) => {
    setError(null);

    try {
      await api.apprenants.delete(id);
      setApprenants(prev => prev.filter(item => item.id !== id));
      return { success: true };
    } catch (err) {
      const message = err.message || 'Erreur lors de la suppression de l\'apprenant';
      setError(message);
      return { success: false, error: message };
    }
  }, []);

  useEffect(() => {
    if (autoLoad) {
      loadApprenants();
    }
  }, [autoLoad, loadApprenants]);

  return {
    apprenants,
    loading,
    error,
    loadApprenants,
    createApprenant,
    updateApprenant,
    deleteApprenant,
  };
};

export default useApprenants;
